import React, { Component } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import { NavLink } from 'react-router-dom';
import {
  saveToken,
  saveRefreshToken,
  retrieveToken,
  clearToken,
  setConsulenteId,
  setSuperconsulente,
  setGruppoId,
} from '../../utils/storage';
import { logIn, getAccountData } from '../../utils/api/account_api';
import { loginMicrosoft } from '../../auth/auth.service';
import LoginResult from './LoginResult';
import LoginForm from './LoginForm';
import MicrosoftIcon from '../../components/icons/MicrosoftIcon';
import AuthPageLayout from '../../layouts/AuthPageLayout';

const styleToUse = (theme) => ({
  card: {
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
  },
  cardHeader: {
    padding: '36px 44px 18px 44px',
    backgroundColor: theme.palette.background.paper,
  },
  title: {
    fontWeight: 800,
    letterSpacing: -0.5,
    marginBottom: 6,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  body: {
    padding: '22px 44px 44px 44px',
    backgroundColor: theme.palette.background.paper,
  },
  sepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '24px 0 18px 0',
  },
  sepText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  sepDivider: {
    flex: 1,
  },
  msButton: {
    height: 48,
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 700,
    letterSpacing: 0,
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0,0,0,0.14)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    '&:hover': {
      backgroundColor: '#ffffff', // niente grigio
      borderColor: theme.palette.primary.main,
      boxShadow: `0 8px 22px ${theme.palette.primary.main}20`,
    },
  },
  msIcon: {
    width: 18,
    height: 18,
    display: 'inline-block',
    marginRight: 10,
    verticalAlign: 'middle',
  },
  errorText: {
    marginTop: 10,
    color: theme.palette.error.main,
    fontSize: 13,
  },
});

class LoginPage extends Component {
  constructor() {
    super();
    this.state = {
      showForm: true,
      loginError: false,
      loginMessage: '',
      userAlreadyLogged: false,

      msalLoading: false,
      msalError: null,
    };
  }

  componentDidMount() {
    // BFCache: user clicks Microsoft login (msalLoading=true), backs out of IdP
    // without choosing an account. The browser restore this page from the
    // back-forward cache with full React state intact — including msalLoading.
    // reset msalLoading to false to avoid showing the loading indicator.
    this.handlePageShow = (event) => {
      if (event.persisted) {
        this.setState({ msalLoading: false });
      }
    };
    window.addEventListener('pageshow', this.handlePageShow);

    const token = retrieveToken();

    if (token !== null) {
      this.setState({
        loginError: true,
        showForm: false,
        loginMessage: 'Eseguire il logout prima di ripetere il login.',
        userAlreadyLogged: true,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  loginWithMicrosoft = async () => {
    try {
      this.setState({ msalLoading: true, msalError: null });
      await loginMicrosoft(); // redirect
    } catch (e) {
      this.setState({
        msalLoading: false,
        msalError: e?.message || 'Impossibile avviare login Microsoft',
      });
    }
  };

  executeLogin = (loginData) => {
    logIn(loginData.username, loginData.password)
      .then(async (result) => {
        let jwtToken = result.token;
        let message = '';
        if (jwtToken !== null) {
          saveToken(jwtToken);
          saveRefreshToken(result.refreshToken);
          try {
            const mod = await import('store/Store');
            const storeState = mod.default.getState();
            storeState.setToken(jwtToken);
            if (
              result.refreshToken &&
              typeof storeState.setRefreshToken === 'function'
            ) {
              storeState.setRefreshToken(result.refreshToken);
            }
          } catch (e) {
            console.warn('[LoginPage] shared store non disponibile:', e);
          }
          this.getUserData();
        } else {
          message = 'Errore di servizio. Per favore, riprovare più tardi.';
          this.setState({
            loginError: true,
            showForm: false,
            loginMessage: message,
          });
        }
      })
      .catch((exception) => {
        let errorMessage = exception.message;
        this.setState({
          loginError: true,
          showForm: false,
          loginMessage: errorMessage,
        });
      });
  };

  getUserData = () => {
    getAccountData()
      .then((result) => {
        let ruoli = result.ruoli;
        let consulenteId = null;
        let superconsulente = false;
        let gruppoId = result.gruppoId;
        ruoli.forEach(function (item, index, array) {
          if (item === 'ROLE_CONSULENTE') {
            consulenteId = result.id;
          } else if (item === 'ROLE_SUPERCONSULENTE') {
            consulenteId = result.id;
            superconsulente = true;
          }
        });
        setConsulenteId(consulenteId);
        setSuperconsulente(superconsulente);
        setGruppoId(gruppoId);

        this.setState({
          loginError: false,
          showForm: false,
          loginMessage: '',
          msalLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          loginError: true,
          showForm: false,
          loginMessage: error.message,
          msalLoading: false,
        });
      });
  };

  returnToForm = () => {
    this.setState({ showForm: true });
  };

  render() {
    const { classes, theme } = this.props;

    return (
      <AuthPageLayout>
        <Paper className={classes.card}>
          <div className={classes.cardHeader}>
            <Typography variant="h4" className={classes.title}>
              Benvenuto
            </Typography>
            {this.state.showForm && (
              <Typography variant="body1" className={classes.subtitle}>
                Accedi per continuare
              </Typography>
            )}
          </div>
          <div className={classes.body}>
            {this.state.showForm ? (
              <>
                {/* Login classico */}
                <LoginForm onLoginButtonPressed={this.executeLogin} />

                <div className={classes.sepRow}>
                  <Divider className={classes.sepDivider} />
                  <span className={classes.sepText}>oppure</span>
                  <Divider className={classes.sepDivider} />
                </div>

                {/* Login Microsoft */}
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  disabled={this.state.msalLoading}
                  className={classes.msButton}
                  onClick={this.loginWithMicrosoft}
                >
                  {this.state.msalLoading ? (
                    <>
                      <CircularProgress size={18} style={{ marginRight: 10 }} />
                      Redirect in corso...
                    </>
                  ) : (
                    <>
                      <MicrosoftIcon className={classes.msIcon} />
                      Accedi con Microsoft
                    </>
                  )}
                </Button>

                {this.state.msalError ? (
                  <div className={classes.errorText}>
                    {this.state.msalError}
                  </div>
                ) : null}
              </>
            ) : this.state.userAlreadyLogged ? (
              <Box textAlign="center" py={2}>
                <Typography style={{ fontWeight: 600 }}>
                  {this.state.loginMessage}
                </Typography>
                <Box mt={2}>
                  <NavLink to={'/'} style={{ textDecoration: 'none' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        clearToken();
                      }}
                      style={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.getContrastText(
                          theme.palette.primary.main,
                        ),
                      }}
                    >
                      Logout
                    </Button>
                  </NavLink>
                </Box>
              </Box>
            ) : (
              <LoginResult
                theme={theme}
                hasError={this.state.loginError}
                message={this.state.loginMessage}
                onBackButtonPressed={this.returnToForm}
              />
            )}
          </div>
        </Paper>
      </AuthPageLayout>
    );
  }
}
export default withStyles(styleToUse, { withTheme: true })(LoginPage);
